import React, { useMemo, useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import { Tooltip } from 'react-tooltip';
import {
  HiOutlineTrendingUp,
  HiOutlineTrendingDown,
  HiOutlineChartBar,
  HiOutlineLightningBolt,
  HiOutlineArrowSmUp,
  HiOutlineArrowSmDown,
  HiOutlineFire,
  HiOutlineGlobeAlt,
  HiOutlineRss,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
} from 'react-icons/hi';
import TokenStack from './TokenStack';
import ChainIcon from './ChainIcon';
import ProtocolIcon from './ProtocolIcon';
import { PROTOCOL_COLORS, CHAIN_COLORS, RISK_MAP } from '../data';
import { fmt } from '../utils';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import { computeChainStats, computeSmartMoneySignals, computeMarketFeed } from '../api';
import 'react-tooltip/dist/react-tooltip.css';

// ── Animation variants ──

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: 'easeOut' },
  }),
};

const bannerVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.2 },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
};

const cardHover = {
  scale: 1.03,
  y: -4,
  transition: { duration: 0.2, ease: 'easeOut' },
};

// ── Shared sub-components ──

function SectionHeader({ icon, label, title, index }) {
  const { colors } = useTheme();
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      custom={index || 0}
      variants={sectionVariants}
      style={{ marginBottom: 20 }}
    >
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        color: colors.labelMuted,
        letterSpacing: 1.5,
        fontWeight: 600,
        marginBottom: 6,
        textTransform: 'uppercase',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
      }}>
        {icon}
        {label}
      </div>
      <div style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: 22,
        color: colors.textPrimary,
      }}>
        {title}
      </div>
    </motion.div>
  );
}

function MoverCard({ pool, onSelect, isMobile, direction }) {
  const { colors } = useTheme();
  const protocolColor = PROTOCOL_COLORS[pool.protocol] || colors.textPrimary;
  const chainColor = CHAIN_COLORS[pool.chain] || colors.textSecondary;
  const pct1D = pool.apyPct1D;
  const pct7D = pool.apyPct7D;
  const isPositive1D = pct1D != null && pct1D > 0;
  const isPositive7D = pct7D != null && pct7D > 0;
  const borderLeftColor = direction === 'up' ? colors.green : direction === 'down' ? colors.red : (isPositive1D ? colors.green : colors.red);
  const tooltipId = `mover-${pool.id}`;

  return (
    <>
      <motion.div
        data-tooltip-id={tooltipId}
        whileHover={{ scale: 1.03, y: -3 }}
        whileInView={{ opacity: 1, x: 0 }}
        initial={{ opacity: 0, x: 30 }}
        viewport={{ once: true, amount: 0.05 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        onClick={() => onSelect(pool)}
        style={{
          flex: '0 0 auto',
          width: isMobile ? 'calc(85vw - 24px)' : 280,
          background: colors.surface,
          borderRadius: 14,
          padding: 20,
          boxShadow: `0 1px 4px ${colors.shadow}`,
          border: `1px solid ${colors.border}`,
          borderLeft: `3px solid ${borderLeftColor}`,
          cursor: 'pointer',
          scrollSnapAlign: 'start',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <TokenStack tokens={pool.tokens} size={26} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 14,
              color: colors.textPrimary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {pool.name}
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 8,
                color: protocolColor,
                background: `${protocolColor}12`,
                border: `1px solid ${protocolColor}30`,
                padding: '1px 5px',
                borderRadius: 3,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
              }}>
                <ProtocolIcon protocol={pool.protocol} size={9} />
                {pool.protocol}
              </span>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 8,
                color: chainColor,
                background: `${chainColor}12`,
                border: `1px solid ${chainColor}30`,
                padding: '1px 5px',
                borderRadius: 3,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
              }}>
                <ChainIcon chain={pool.chain} size={9} />
                {pool.chain}
              </span>
            </div>
          </div>
        </div>

        <div style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: 28,
          color: pool.apr > 20 ? colors.gold : colors.green,
          lineHeight: 1,
          marginBottom: 10,
        }}>
          <CountUp end={pool.apr || 0} decimals={2} duration={1.2} suffix="%" preserveValue />
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
          {pct1D != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: colors.labelLight, letterSpacing: 0.5 }}>1D</span>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 600,
                color: isPositive1D ? colors.green : colors.red,
                display: 'inline-flex', alignItems: 'center', gap: 2,
              }}>
                {isPositive1D ? <HiOutlineArrowSmUp size={14} /> : <HiOutlineArrowSmDown size={14} />}
                <CountUp end={Math.abs(pct1D)} decimals={1} duration={1} suffix="%" preserveValue />
              </span>
            </div>
          )}
          {pct7D != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: colors.labelLight, letterSpacing: 0.5 }}>7D</span>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 600,
                color: isPositive7D ? colors.green : colors.red,
                display: 'inline-flex', alignItems: 'center', gap: 2,
              }}>
                {isPositive7D ? <HiOutlineArrowSmUp size={14} /> : <HiOutlineArrowSmDown size={14} />}
                <CountUp end={Math.abs(pct7D)} decimals={1} duration={1} suffix="%" preserveValue />
              </span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.labelLight,
            letterSpacing: 1, display: 'inline-flex', alignItems: 'center', gap: 3,
          }}>
            <HiOutlineChartBar size={11} style={{ opacity: 0.6 }} /> TVL
          </span>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.textSecondary, fontWeight: 500 }}>
            {fmt(pool.tvl)}
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, fontWeight: 600,
            color: RISK_MAP[pool.risk].color,
            background: RISK_MAP[pool.risk].bg,
            border: `1px solid ${RISK_MAP[pool.risk].border}`,
            padding: '2px 8px', borderRadius: 3,
          }}>
            {pool.risk}
          </span>
        </div>
      </motion.div>
      <Tooltip
        id={tooltipId}
        place="top"
        delayShow={300}
        style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, borderRadius: 8,
          padding: '10px 14px', zIndex: 100, maxWidth: 260, lineHeight: 1.5,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{pool.name}</div>
        <div>APR: {(pool.apr || 0).toFixed(2)}%</div>
        <div>TVL: {fmt(pool.tvl)}</div>
        <div>Vol 24h: {fmt(pool.volume24h)}</div>
        <div>Fees 24h: {fmt(pool.fees24h)}</div>
        <div>Risk: {pool.risk} &middot; {pool.protocol} &middot; {pool.chain}</div>
      </Tooltip>
    </>
  );
}

function HorizontalCarousel({ children, isMobile }) {
  const scrollRef = useRef(null);
  const { colors } = useTheme();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 300, behavior: 'smooth' });
  };

  const arrowStyle = (side) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    [side]: -14,
    width: 32, height: 32, borderRadius: '50%',
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    boxShadow: `0 2px 8px ${colors.shadow}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    fontFamily: "'IBM Plex Mono', monospace", fontSize: 14,
    color: colors.textSecondary, zIndex: 2,
    transition: 'background 0.15s, box-shadow 0.15s',
  });

  const gradientFade = (side) => ({
    position: 'absolute',
    top: 0, bottom: 8, [side]: 0, width: 40,
    background: side === 'left'
      ? `linear-gradient(to right, ${colors.bg || '#F8F9FA'}, transparent)`
      : `linear-gradient(to left, ${colors.bg || '#F8F9FA'}, transparent)`,
    pointerEvents: 'none', zIndex: 1,
    borderRadius: side === 'left' ? '14px 0 0 14px' : '0 14px 14px 0',
  });

  return (
    <div style={{ position: 'relative' }}>
      {canScrollLeft && <div style={gradientFade('left')} />}
      {!isMobile && canScrollLeft && (
        <div
          style={arrowStyle('left')}
          onClick={() => scroll(-1)}
          onMouseEnter={e => { e.currentTarget.style.background = colors.hover; }}
          onMouseLeave={e => { e.currentTarget.style.background = colors.surface; }}
        >
          {'\u2039'}
        </div>
      )}
      <div
        ref={scrollRef}
        style={{
          display: 'flex', gap: isMobile ? 12 : 16,
          overflowX: 'auto', scrollSnapType: 'x mandatory', scrollBehavior: 'smooth',
          paddingBottom: 8, paddingTop: 4, paddingLeft: 2, paddingRight: 2,
          msOverflowStyle: 'none', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </div>
      {!isMobile && canScrollRight && (
        <div
          style={arrowStyle('right')}
          onClick={() => scroll(1)}
          onMouseEnter={e => { e.currentTarget.style.background = colors.hover; }}
          onMouseLeave={e => { e.currentTarget.style.background = colors.surface; }}
        >
          {'\u203A'}
        </div>
      )}
      {canScrollRight && <div style={gradientFade('right')} />}
    </div>
  );
}

// ── Section 2: Trending Coins ──

function TrendingCoinsSection({ trending, isMobile }) {
  const { colors } = useTheme();

  if (!trending || trending.length === 0) return null;

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={sectionVariants}
      custom={1}
      style={{ marginBottom: 36 }}
    >
      <SectionHeader
        icon={<HiOutlineFire size={12} style={{ color: colors.gold }} />}
        label="COINGECKO"
        title="Trending on CoinGecko"
        index={1}
      />
      <HorizontalCarousel isMobile={isMobile}>
        {trending.map((coin) => (
          <motion.div
            key={coin.id}
            whileHover={cardHover}
            style={{
              flex: '0 0 auto',
              width: isMobile ? 160 : 180,
              background: colors.surface,
              borderRadius: 14,
              padding: 16,
              boxShadow: `0 1px 4px ${colors.shadow}`,
              border: `1px solid ${colors.border}`,
              cursor: 'default',
              scrollSnapAlign: 'start',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              {coin.thumb && (
                <img
                  src={coin.thumb}
                  alt={coin.symbol}
                  style={{ width: 28, height: 28, borderRadius: '50%' }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: 13,
                  color: colors.textPrimary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {coin.name}
                </div>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9,
                  color: colors.labelMuted,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}>
                  {coin.symbol}
                </div>
              </div>
            </div>
            {coin.marketCapRank && (
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                color: colors.textSecondary,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ color: colors.labelLight, letterSpacing: 0.5 }}>RANK</span>
                <span style={{ fontWeight: 600 }}>#{coin.marketCapRank}</span>
              </div>
            )}
            {coin.score != null && (
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                color: colors.textSecondary,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 4,
              }}>
                <span style={{ color: colors.labelLight, letterSpacing: 0.5 }}>SCORE</span>
                <span style={{ fontWeight: 600, color: colors.gold }}>#{coin.score + 1}</span>
              </div>
            )}
          </motion.div>
        ))}
      </HorizontalCarousel>
    </motion.div>
  );
}

// ── Section 3: Chain Heatmap ──

function ChainHeatmapSection({ pools, isMobile }) {
  const { colors } = useTheme();
  const chainStats = useMemo(() => computeChainStats(pools), [pools]);
  const totalTvl = useMemo(() => chainStats.reduce((s, c) => s + c.totalTvl, 0), [chainStats]);

  if (chainStats.length === 0) return null;

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={sectionVariants}
      custom={2}
      style={{ marginBottom: 36 }}
    >
      <SectionHeader
        icon={<HiOutlineGlobeAlt size={12} style={{ color: colors.green }} />}
        label="TVL DISTRIBUTION"
        title="Chain Heatmap"
        index={2}
      />
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 12,
        }}
      >
        {chainStats.map((stat) => {
          const chainColor = CHAIN_COLORS[stat.chain] || colors.textSecondary;
          const pct = totalTvl > 0 ? (stat.totalTvl / totalTvl) * 100 : 0;
          return (
            <motion.div
              key={stat.chain}
              variants={staggerItem}
              whileHover={cardHover}
              style={{
                background: colors.surface,
                borderRadius: 12,
                padding: 16,
                border: `1px solid ${colors.border}`,
                borderLeft: `3px solid ${chainColor}`,
                boxShadow: `0 1px 4px ${colors.shadow}`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Background bar showing TVL proportion */}
              <div style={{
                position: 'absolute',
                left: 0, bottom: 0,
                width: `${Math.max(pct, 5)}%`,
                height: 3,
                background: chainColor,
                opacity: 0.4,
                borderRadius: '0 3px 0 0',
              }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <ChainIcon chain={stat.chain} size={18} />
                <span style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: 14,
                  color: colors.textPrimary,
                }}>
                  {stat.chain}
                </span>
              </div>

              <div style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: 22,
                color: chainColor,
                lineHeight: 1,
                marginBottom: 8,
              }}>
                <CountUp
                  end={stat.totalTvl / 1e6}
                  decimals={stat.totalTvl >= 1e9 ? 0 : 1}
                  duration={1.5}
                  prefix="$"
                  suffix="M"
                  preserveValue
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10, color: colors.labelLight, letterSpacing: 0.5,
                }}>
                  <CountUp end={stat.poolCount} duration={1} preserveValue /> pools
                </span>
                <span style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10, color: colors.green, fontWeight: 600,
                }}>
                  <CountUp end={stat.avgApr} decimals={1} duration={1} suffix="%" preserveValue /> avg
                </span>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </motion.div>
  );
}

// ── Section 4: Smart Money Signals ──

function SmartMoneySection({ pools, onSelectPool, isMobile }) {
  const { colors } = useTheme();
  const signals = useMemo(() => computeSmartMoneySignals(pools), [pools]);

  if (signals.length === 0) return null;

  const typeConfig = {
    volume_spike: { color: colors.gold, bg: colors.goldBg, border: colors.goldBorder, icon: <HiOutlineLightningBolt size={16} /> },
    apr_surge: { color: colors.green, bg: colors.greenBg, border: colors.greenBorder, icon: <HiOutlineTrendingUp size={16} /> },
    tvl_spike: { color: colors.green, bg: colors.greenBg, border: colors.greenBorder, icon: <HiOutlineChartBar size={16} /> },
    new_high: { color: colors.gold, bg: colors.goldBg, border: colors.goldBorder, icon: <HiOutlineTrendingUp size={16} /> },
  };
  const defaultSignalConfig = { color: colors.green, bg: colors.greenBg, border: colors.greenBorder, icon: <HiOutlineTrendingUp size={16} /> };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={sectionVariants}
      custom={3}
      style={{ marginBottom: 36 }}
    >
      <SectionHeader
        icon={<HiOutlineLightningBolt size={12} style={{ color: colors.gold }} />}
        label="UNUSUAL ACTIVITY"
        title="Smart Money Signals"
        index={3}
      />
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 12,
        }}
      >
        {signals.map((signal, idx) => {
          const cfg = typeConfig[signal.type] || defaultSignalConfig;
          return (
            <motion.div
              key={idx}
              variants={staggerItem}
              whileHover={cardHover}
              onClick={() => signal.pool && onSelectPool(signal.pool)}
              style={{
                background: colors.surface,
                borderRadius: 12,
                padding: 16,
                border: `1px solid ${colors.border}`,
                borderLeft: `3px solid ${cfg.color}`,
                boxShadow: `0 1px 4px ${colors.shadow}`,
                cursor: signal.pool ? 'pointer' : 'default',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: cfg.bg,
                  border: `1px solid ${cfg.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: cfg.color, flexShrink: 0,
                }}>
                  {cfg.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'DM Serif Display', serif",
                    fontSize: 14,
                    color: colors.textPrimary,
                    marginBottom: 4,
                  }}>
                    {signal.pool ? signal.pool.name : 'Signal'}
                  </div>
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    color: colors.textSecondary,
                    lineHeight: 1.5,
                  }}>
                    {signal.description}
                  </div>
                  {/* Magnitude bar */}
                  <div style={{
                    marginTop: 8,
                    height: 3,
                    background: colors.scoreTrack,
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}>
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${Math.min(signal.magnitude / (signals[0]?.magnitude || 1) * 100, 100)}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      style={{
                        height: '100%',
                        background: cfg.color,
                        borderRadius: 2,
                      }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </motion.div>
  );
}

// ── Section 5: Market Feed ──

function MarketFeedSection({ pools, onSelectPool, isMobile }) {
  const { colors } = useTheme();
  const feed = useMemo(() => computeMarketFeed(pools), [pools]);

  if (feed.length === 0) return null;

  const feedTypeConfig = {
    new_opportunity: { color: colors.green, icon: <HiOutlineTrendingUp size={14} /> },
    whale_activity: { color: colors.gold, icon: <HiOutlineLightningBolt size={14} /> },
    yield_alert: { color: colors.green, icon: <HiOutlineChartBar size={14} /> },
    protocol_growth: { color: colors.textSecondary, icon: <HiOutlineGlobeAlt size={14} /> },
  };
  const defaultFeedConfig = { color: colors.green, icon: <HiOutlineChartBar size={14} /> };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={sectionVariants}
      custom={4}
      style={{ marginBottom: 36 }}
    >
      <SectionHeader
        icon={<HiOutlineRss size={12} style={{ color: colors.green }} />}
        label="LIVE ACTIVITY"
        title="What's Happening"
        index={4}
      />
      <div style={{ position: 'relative', paddingLeft: 24 }}>
        {/* Timeline line */}
        <div style={{
          position: 'absolute',
          left: 7,
          top: 0,
          bottom: 0,
          width: 2,
          background: colors.border,
          borderRadius: 1,
        }} />
        <AnimatePresence>
          {feed.map((item, idx) => {
            const cfg = feedTypeConfig[item.type] || defaultFeedConfig;
            const itemColor = item.color || cfg.color;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.4, delay: idx * 0.06 }}
                onClick={() => item.pool && onSelectPool(item.pool)}
                style={{
                  position: 'relative',
                  marginBottom: 16,
                  cursor: item.pool ? 'pointer' : 'default',
                }}
              >
                {/* Timeline dot */}
                <div style={{
                  position: 'absolute',
                  left: -21,
                  top: 4,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: itemColor,
                  border: `2px solid ${colors.surface}`,
                  boxShadow: `0 0 0 2px ${colors.border}`,
                }} />

                <div style={{
                  background: colors.surface,
                  borderRadius: 10,
                  padding: isMobile ? '12px 14px' : '14px 18px',
                  border: `1px solid ${colors.border}`,
                  boxShadow: `0 1px 4px ${colors.shadow}`,
                  transition: 'box-shadow 0.15s',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ color: itemColor }}>{cfg.icon}</span>
                    <span style={{
                      fontFamily: "'DM Serif Display', serif",
                      fontSize: 13,
                      color: colors.textPrimary,
                      flex: 1,
                    }}>
                      {item.title}
                    </span>
                    <span style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 9,
                      color: colors.labelLight,
                      letterSpacing: 0.5,
                      flexShrink: 0,
                    }}>
                      now
                    </span>
                  </div>
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    color: colors.textSecondary,
                    lineHeight: 1.4,
                  }}>
                    {item.description}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// ── Section 6: APR Movers (collapsible) ──

function AprMoversSection({ pools, onSelectPool, isMobile }) {
  const { colors } = useTheme();
  const [openSections, setOpenSections] = useState({ up: true, down: false, weekly: false });

  const topMoversUp = useMemo(() =>
    pools.filter(p => p.apyPct1D != null && p.apyPct1D > 2)
      .sort((a, b) => b.apyPct1D - a.apyPct1D).slice(0, 15),
    [pools]
  );

  const topMoversDown = useMemo(() =>
    pools.filter(p => p.apyPct1D != null && p.apyPct1D < -2)
      .sort((a, b) => a.apyPct1D - b.apyPct1D).slice(0, 15),
    [pools]
  );

  const weeklyMovers = useMemo(() =>
    pools.filter(p => p.apyPct7D != null && Math.abs(p.apyPct7D) > 5)
      .sort((a, b) => Math.abs(b.apyPct7D) - Math.abs(a.apyPct7D)).slice(0, 15),
    [pools]
  );

  const hasContent = topMoversUp.length > 0 || topMoversDown.length > 0 || weeklyMovers.length > 0;
  if (!hasContent) return null;

  const toggle = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const collapsibleHeader = (key, label, sublabel, icon, count, accentColor) => (
    <div
      onClick={() => toggle(key)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        background: colors.surface,
        borderRadius: openSections[key] ? '10px 10px 0 0' : 10,
        border: `1px solid ${colors.border}`,
        borderBottom: openSections[key] ? `1px solid ${colors.borderLight}` : `1px solid ${colors.border}`,
        cursor: 'pointer',
        marginBottom: openSections[key] ? 0 : 12,
        transition: 'border-radius 0.2s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: accentColor }}>{icon}</span>
        <div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10, color: colors.labelMuted, letterSpacing: 1.5,
            fontWeight: 600, textTransform: 'uppercase',
          }}>
            {sublabel}
          </div>
          <div style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 16, color: colors.textPrimary,
          }}>
            {label}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11, color: accentColor, fontWeight: 600,
        }}>
          {count}
        </span>
        {openSections[key]
          ? <HiOutlineChevronUp size={16} style={{ color: colors.labelLight }} />
          : <HiOutlineChevronDown size={16} style={{ color: colors.labelLight }} />
        }
      </div>
    </div>
  );

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={sectionVariants}
      custom={5}
      style={{ marginBottom: 36 }}
    >
      <SectionHeader
        icon={<HiOutlineChartBar size={12} style={{ color: colors.gold }} />}
        label="APR CHANGES"
        title="APR Movers"
        index={5}
      />

      {/* Movers Up */}
      {topMoversUp.length > 0 && (
        <div style={{ marginBottom: openSections.up ? 16 : 0 }}>
          {collapsibleHeader('up', 'Top Movers Up', 'BIGGEST APR GAINS (24H)', <HiOutlineTrendingUp size={18} />, topMoversUp.length, colors.green)}
          <AnimatePresence>
            {openSections.up && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  overflow: 'hidden',
                  background: colors.surface,
                  borderRadius: '0 0 10px 10px',
                  border: `1px solid ${colors.border}`,
                  borderTop: 'none',
                  padding: '16px 16px 8px',
                  marginBottom: 12,
                }}
              >
                <HorizontalCarousel isMobile={isMobile}>
                  {topMoversUp.map(pool => (
                    <MoverCard key={pool.id} pool={pool} onSelect={onSelectPool} isMobile={isMobile} direction="up" />
                  ))}
                </HorizontalCarousel>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Movers Down */}
      {topMoversDown.length > 0 && (
        <div style={{ marginBottom: openSections.down ? 16 : 0 }}>
          {collapsibleHeader('down', 'Top Movers Down', 'BIGGEST APR DROPS (24H)', <HiOutlineTrendingDown size={18} />, topMoversDown.length, colors.red)}
          <AnimatePresence>
            {openSections.down && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  overflow: 'hidden',
                  background: colors.surface,
                  borderRadius: '0 0 10px 10px',
                  border: `1px solid ${colors.border}`,
                  borderTop: 'none',
                  padding: '16px 16px 8px',
                  marginBottom: 12,
                }}
              >
                <HorizontalCarousel isMobile={isMobile}>
                  {topMoversDown.map(pool => (
                    <MoverCard key={pool.id} pool={pool} onSelect={onSelectPool} isMobile={isMobile} direction="down" />
                  ))}
                </HorizontalCarousel>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Weekly Movers */}
      {weeklyMovers.length > 0 && (
        <div style={{ marginBottom: openSections.weekly ? 16 : 0 }}>
          {collapsibleHeader('weekly', 'Weekly Movers', 'BIGGEST APR CHANGES (7D)', <HiOutlineChartBar size={18} />, weeklyMovers.length, colors.gold)}
          <AnimatePresence>
            {openSections.weekly && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  overflow: 'hidden',
                  background: colors.surface,
                  borderRadius: '0 0 10px 10px',
                  border: `1px solid ${colors.border}`,
                  borderTop: 'none',
                  padding: '16px 16px 8px',
                  marginBottom: 12,
                }}
              >
                <HorizontalCarousel isMobile={isMobile}>
                  {weeklyMovers.map(pool => (
                    <MoverCard key={pool.id} pool={pool} onSelect={onSelectPool} isMobile={isMobile} />
                  ))}
                </HorizontalCarousel>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
}

// ── Main export ──

export default function TrendingView({ pools, trending, onSelectPool }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();

  // Banner stats
  const totalPools = pools.length;
  const totalTvl = useMemo(() => pools.reduce((s, p) => s + (p.tvl || 0), 0), [pools]);
  const chainsTracked = useMemo(() => new Set(pools.map(p => p.chain)).size, [pools]);
  const protocolsTracked = useMemo(() => new Set(pools.map(p => p.protocol)).size, [pools]);

  return (
    <div style={{ padding: isMobile ? '12px 12px' : '20px 24px', maxWidth: 1200, margin: '0 auto' }}>

      {/* ── Section 1: Banner ── */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={bannerVariants}
        style={{
          background: colors.bannerGradient,
          borderRadius: 12,
          padding: isMobile ? '20px 16px' : '28px 32px',
          marginBottom: isMobile ? 20 : 28,
          border: `1px solid ${colors.greenBorder}`,
        }}
      >
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: colors.green,
          letterSpacing: 1.5,
          fontWeight: 700,
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          textTransform: 'uppercase',
        }}>
          <HiOutlineGlobeAlt size={13} /> MARKET OVERVIEW
        </div>
        <div style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: isMobile ? 24 : 30,
          color: colors.textPrimary,
          marginBottom: 6,
        }}>
          Market Overview
        </div>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.textSecondary,
          marginBottom: 18,
        }}>
          Real-time DeFi market intelligence
        </div>

        {/* Animated stats row */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          style={{ display: 'flex', gap: isMobile ? 16 : 32, flexWrap: 'wrap' }}
        >
          {[
            { label: 'POOLS', value: totalPools, color: colors.textPrimary, suffix: '' },
            { label: 'TOTAL TVL', value: totalTvl / 1e9, color: colors.green, suffix: 'B', prefix: '$', decimals: 2 },
            { label: 'CHAINS', value: chainsTracked, color: colors.textPrimary, suffix: '' },
            { label: 'PROTOCOLS', value: protocolsTracked, color: colors.textPrimary, suffix: '' },
          ].map((stat) => (
            <motion.div key={stat.label} variants={staggerItem} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: 20,
                color: stat.color,
              }}>
                <CountUp
                  end={stat.value}
                  decimals={stat.decimals || 0}
                  duration={1.5}
                  prefix={stat.prefix || ''}
                  suffix={stat.suffix}
                  preserveValue
                />
              </span>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                color: colors.labelMuted,
                letterSpacing: 1,
                textTransform: 'uppercase',
              }}>
                {stat.label}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* ── Section 2: CoinGecko Trending ── */}
      <TrendingCoinsSection trending={trending} isMobile={isMobile} />

      {/* ── Section 3: Chain Heatmap ── */}
      <ChainHeatmapSection pools={pools} isMobile={isMobile} />

      {/* ── Section 4: Smart Money Signals ── */}
      <SmartMoneySection pools={pools} onSelectPool={onSelectPool} isMobile={isMobile} />

      {/* ── Section 5: Market Feed ── */}
      <MarketFeedSection pools={pools} onSelectPool={onSelectPool} isMobile={isMobile} />

      {/* ── Section 6: APR Movers (collapsible) ── */}
      <AprMoversSection pools={pools} onSelectPool={onSelectPool} isMobile={isMobile} />

      {/* Empty state */}
      {pools.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          style={{
            textAlign: 'center',
            padding: '60px 0',
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            color: colors.labelLight,
          }}
        >
          No market data available right now. Check back soon.
        </motion.div>
      )}
    </div>
  );
}
